import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSessionStore } from '@features/auth/store/useSessionStore';
import useContactsApi from '@features/contacts/hooks/useContactsApi';
import { logError } from '@lib/logger/logError';
import { logInfo } from '@lib/logger/logInfo';
import { logSuccess } from '@lib/logger/logSuccess';
import { logWarning } from '@lib/logger/logWarning';
import {
  type ContactInfoDto,
  type ContactListDto,
  type ContactsTab,
  type CreateContactListDto,
  type LoadingState,
  type UpdateContactDto
} from '@features/contacts/types/contacts.types';
import { useOutreachViewModel } from '@features/contacts/hooks/useOutreachViewModel.ts';
import { useSalesforceViewModel } from '@features/contacts/hooks/useSalesforceViewModel.ts';
import { useHubspotViewModel } from '@features/contacts/hooks/useHubspotViewModel.ts';
import { useTranslation } from 'react-i18next';

export type { ContactsTab, LoadingState } from '@features/contacts/types/contacts.types';
export interface ContactsCacheEntry {
  listId: string;
  contacts: ContactInfoDto[];
  fetchedAt: number;
}

export interface UseContactsViewModel {
  organizationId: string;
  activeTab: ContactsTab;
  setActiveTab: (tab: ContactsTab) => void;
  contactLists: ContactListDto[];
  contactListsLoadingState: LoadingState;
  totalCsvContactLists: number;
  selectedContactListId: string | null;
  setSelectedContactListId: (contactListId: string | null) => void;
  selectedContactList: ContactListDto | null;
  contacts: ContactInfoDto[];
  contactsLoadingState: LoadingState;
  totalContacts: number;
  totalWarnings: number;
  totalOk: number;
  refreshContactLists: (page?: string, pageLimit?: string, searchQuery?: string) => Promise<void>;
  refreshContacts: (options?: { force?: boolean }, resync?: boolean) => Promise<void>;
  createContactList: (createData: CreateContactListDto) => Promise<string | null>;
  deleteContactList: (contactListId: string) => Promise<void>;
  updateContact: (contactUpdateData: UpdateContactDto) => Promise<void>;
  updateContactFuegoLead: (contactId: string, flagged: boolean) => Promise<void>;
  deleteContact: (contactId: string) => Promise<void>;
  uploadCsvToContactList: (params: { organizationId: string; contactListId: string; file: File }) => Promise<void>;
  downloadTemplateCsv: () => void;
  exportContactsToCsv: (params: { contacts: ContactInfoDto[]; filename?: string }) => void;
  invalidateContactsCache: (contactListId?: string) => void;
  retrieveCsvLists: (page?: string, pageLimit?: string, searchQuery?: string) => Promise<ContactListDto[] | undefined>,
}

export default function useContactsViewModel (): UseContactsViewModel {
  const { t } = useTranslation();
  const currentUser = useSessionStore((sessionState) => sessionState.currentUser);
  const organizationId = String(currentUser?.organizationId ?? '').trim();
  const contactsApi = useContactsApi();
  const { retrieveOutreachLists, retrieveOutreachContacts, nonce: outreachNonce } = useOutreachViewModel();
  const { retrieveSalesforceLists, retrieveSalesforceContacts, nonce: salesforceNonce } = useSalesforceViewModel();
  const { retrieveHubspotContactsLists, retrieveHubspotContacts } = useHubspotViewModel();

  const [activeTab, setActiveTab] = useState<ContactsTab>('csv');

  const [contactLists, setContactLists] = useState<ContactListDto[]>([]);
  const [contactListsLoadingState, setContactListsLoadingState] = useState<LoadingState>('IDLE');
  const [totalCsvContactLists, setTotalCsvContactLists] = useState(0);

  const [selectedContactListId, setSelectedContactListId] = useState<string | null>(null);

  const [contacts, setContacts] = useState<ContactInfoDto[]>([]);
  const [contactsLoadingState, setContactsLoadingState] = useState<LoadingState>('IDLE');

  const contactsCacheRef = useRef<Record<string, ContactsCacheEntry>>({});
  const latestSelectedContactListIdRef = useRef<string | null>(null);

  useEffect(() => {
    latestSelectedContactListIdRef.current = selectedContactListId;
  }, [selectedContactListId]);

  const selectedContactList = useMemo(() => {
    const contactListId = String(selectedContactListId ?? '').trim();
    if (contactListId === '') {
      return null;
    }
    return contactLists.find((list) => list.id === contactListId) ?? null;
  }, [contactLists, selectedContactListId]);

  const totalContacts = useMemo(() => (Array.isArray(contacts) ? contacts.length : 0), [contacts]);

  const totalWarnings = useMemo(() => {
    const contactsArray = Array.isArray(contacts) ? contacts : [];
    return contactsArray.filter((contact) => Array.isArray(contact.errorMessages) && contact.errorMessages.length > 0).length;
  }, [contacts]);

  const totalOk = useMemo(() => Math.max(0, totalContacts - totalWarnings), [totalContacts, totalWarnings]);

  const invalidateContactsCache = useCallback((contactListId?: string) => {
    if (contactListId !== undefined && contactListId.trim() !== '') {
      const cache = contactsCacheRef.current;
      const newCache: Record<string, ContactsCacheEntry> = {};
      for (const cacheKey of Object.keys(cache)) {
        if (cacheKey !== contactListId) {
          newCache[cacheKey] = cache[cacheKey];
        }
      }
      contactsCacheRef.current = newCache;
      return;
    }
    contactsCacheRef.current = {};
  }, []);

  async function retrieveCsvLists (
    page = '1',
    pageLimit = '100',
    searchQuery = '',
  ): Promise<ContactListDto[] | undefined> {
    const response = await contactsApi.getCsvListsByOrgIdAsync(organizationId, page, pageLimit, searchQuery);
    if (!response.success) {
      return undefined;
    }
    setTotalCsvContactLists(response.totalCount ?? response.data?.length ?? 0);
    return response?.data;
  }

  /**
   * Strategy Function to select the appropriate fetch function based on the active tab,
   * callbacks must return ContactListDto[] | undefined to avoid further data adaptations.
   * Add cases for other integrations when needed.
   **/
  const selectContactsListsFetchCallback: (page?: string, pageLimit?: string, searchQuery?: string) => Promise<ContactListDto[] | undefined> = useCallback(async (page, pageLimit, searchQuery)=> {
    switch (activeTab) {
    case 'outreach':
      return await retrieveOutreachLists();

    case 'salesforce':
      return await retrieveSalesforceLists();

    case 'hubspot':
      return await retrieveHubspotContactsLists();

    default: return await retrieveCsvLists(page, pageLimit, searchQuery);
    }
  }, [activeTab, retrieveOutreachLists, retrieveHubspotContactsLists, retrieveSalesforceLists]);

  const refreshContactLists = useCallback(async (
    page = '1',
    pageLimit = '100',
    searchQuery = '',
  ): Promise<void> => {
    if (organizationId === '') {
      setContactLists([]);
      setSelectedContactListId(null);
      return;
    }

    setContactListsLoadingState('LOADING');
    await logInfo('Loading contact lists...', 'ContactsVM.refreshContactLists', { organizationId, activeTab }, false);

    try {
      const response = await selectContactsListsFetchCallback(page, pageLimit, searchQuery);
      if (response === undefined) {
        await logError('Error loading contact lists', refreshContactLists.name, response, false);
        setContactLists([]);
        setContactListsLoadingState('SUCCESS');
        return;
      }

      const fetchedContactLists = response ?? [];
      setContactLists(fetchedContactLists);

      setSelectedContactListId(fetchedContactLists[0]?.id ?? null);

      if (fetchedContactLists.length === 0) {
        setContactListsLoadingState('SUCCESS');
      }

      setContactListsLoadingState('SUCCESS');
      await logSuccess('Contact lists loaded', 'ContactsVM.refreshContactLists', { count: fetchedContactLists.length }, false);
    } catch (error) {
      setContactLists([]);
      setSelectedContactListId(null);
      setContactListsLoadingState('ERROR');

      await logError(
        'Error loading contact lists',
        'ContactsVM.refreshContactLists',
        { organizationId, activeTab, error: String((error as Error)?.message ?? error) },
        false,
        true,
      );
    }
  }, [organizationId, activeTab, selectContactsListsFetchCallback, setContactListsLoadingState]);

  async function retrieveContacts (contactListId: string): Promise<ContactInfoDto[]> {
    const response = await contactsApi.getContactsByContactListIdAsync(contactListId);

    if (!response.success) {
      return [];
    }
    return response.data;
  }

  /**
   * Strategy Callback to select the appropriate fetch function based on the active tab,
   * callbacks must return ContactInfoDto[] | undefined to avoid further data adaptations.
   * Add cases for other integrations when needed.
   **/
  const selectContactsFetchCallback = useCallback(async (activeTab: string, contactsListId: string, resync: boolean): Promise<ContactInfoDto[] | undefined>=> {

    switch (activeTab) {
    case 'outreach': {
      const selectedSequence = contactLists.find((list) => list.id === contactsListId);
      if ((selectedSequence?.sequenceId) == null) {
        return undefined;
      }

      return await retrieveOutreachContacts(outreachNonce, selectedSequence.sequenceId, Number(contactsListId), resync);
    }

    case 'hubspot': {
      const selectedContactsList = contactLists.find((list) => list.id === contactsListId);
      if ((selectedContactsList?.id) == null) {
        return undefined;
      }

      return await retrieveHubspotContacts(selectedContactsList.id);
    }

    case 'salesforce': {
      return await retrieveSalesforceContacts(
        salesforceNonce,
        contactsListId,
      );
    }
    default: return await retrieveContacts(contactsListId);
    }
  }, [activeTab, contactLists]);

  const refreshContacts = useCallback(async (options?: { force?: boolean }, resync?: boolean): Promise<void> => {
    const contactListId = latestSelectedContactListIdRef.current;
    if (contactListId === null || contactListId === '') {
      setContacts([]);
      setContactsLoadingState('IDLE');
      return;
    }

    const cachedEntry = contactsCacheRef.current[contactListId];
    const shouldUseCache = options?.force !== true && cachedEntry !== undefined;

    if (shouldUseCache) {
      setContacts(cachedEntry.contacts);
      setContactsLoadingState('SUCCESS');
      await logInfo('Using cached contacts', 'ContactsVM.refreshContacts.cache', { contactListId, count: cachedEntry.contacts.length }, false);
      return;
    }

    setContactsLoadingState('LOADING');

    try {
      const response = await selectContactsFetchCallback(activeTab, contactListId, resync ?? false);
      if (response === undefined || !Array.isArray(response)) {
        return;
      }

      const fetchedContacts = response;
      contactsCacheRef.current[contactListId] = { listId: contactListId, contacts: fetchedContacts, fetchedAt: Date.now() };

      setContacts(fetchedContacts);
      setContactsLoadingState('SUCCESS');

      await logSuccess('Contacts loaded', 'ContactsVM.refreshContacts', { contactListId, count: fetchedContacts?.length }, false);
    } catch (error) {
      setContacts([]);
      setContactsLoadingState('ERROR');

      await logError(
        (error as Error)?.message ?? 'Error loading contacts',
        'ContactsVM.refreshContacts',
        { contactListId, activeTab, error: String((error as Error)?.message ?? error) },
        false,
      );
    }
  }, [selectContactsFetchCallback, activeTab]);

  const createContactList = useCallback(async (createData: CreateContactListDto): Promise<string | null> => {
    if (organizationId === '') {
      await logWarning(t('contacts.toast.warning.orgMissing'), 'ContactsVM.createContactList.validation', {}, true);
      return null;
    }

    await logInfo('Creating contact list...', 'ContactsVM.createContactList', { createData }, false);

    const response = await contactsApi.createCsvListAsync(createData);
    if (!response.success) {
      await logError(
        response.error?.message ?? t('contacts.toast.error.contactListCreate'),
        'ContactsVM.createContactList',
        { createData, error: response.error?.message },
        true,
        true,
      );
      return null;
    }

    const newContactListId = response.data?.id ?? null;

    await logSuccess(t('contacts.toast.success.contactListCreate'), 'ContactsVM.createContactList', { id: newContactListId }, true);

    await refreshContactLists();
    if (newContactListId !== null && newContactListId !== '') {
      setSelectedContactListId(newContactListId);
    }

    return newContactListId;
  }, [contactsApi, organizationId, refreshContactLists]);

  const deleteContactList = useCallback(async (contactListId: string): Promise<void> => {
    const trimmedContactListId = contactListId.trim();
    if (trimmedContactListId === '') {
      return;
    }

    await logInfo('Deleting contact list...', 'ContactsVM.deleteContactList', { contactListId: trimmedContactListId }, false);

    const response = await contactsApi.deleteContactListAsync(trimmedContactListId);
    if (!response.success) {
      await logError(
        response.error?.message ?? t('contacts.toast.error.contactListDelete'),
        'ContactsVM.deleteContactList',
        { contactListId: trimmedContactListId, error: response.error?.message },
        true,
        true,
      );
      return;
    }

    invalidateContactsCache(trimmedContactListId);

    if (selectedContactListId === trimmedContactListId) {
      setSelectedContactListId(null);
      setContacts([]);
    }

    await logSuccess(t('contacts.toast.success.contactListDelete'), 'ContactsVM.deleteContactList', { contactListId: trimmedContactListId }, true);
    await refreshContactLists();
  }, [contactsApi, invalidateContactsCache, refreshContactLists, selectedContactListId]);

  const updateContact = useCallback(async (contactUpdateData: UpdateContactDto): Promise<void> => {
    const contactId = contactUpdateData.id?.trim() ?? '';
    if (contactId === '') {
      return;
    }

    await logInfo('Updating contact...', 'ContactsVM.updateContact', { contactId }, false);

    const response = await contactsApi.updateContactAsync(contactUpdateData);
    if (!response.success) {
      await logError(
        response.error?.message ?? t('contacts.toast.error.contactUpdate'),
        'ContactsVM.updateContact',
        { contactId, error: response.error?.message },
        true,
        true,
      );
      return;
    }

    invalidateContactsCache(latestSelectedContactListIdRef.current ?? undefined);
    await refreshContacts( { force: true });

    await logSuccess(t('contacts.toast.success.contactUpdate'), 'ContactsVM.updateContact', { contactId }, true);
  }, [contactsApi, invalidateContactsCache, refreshContacts]);

  const updateContactFuegoLead = useCallback(async (contactId: string, flagged: boolean): Promise<void> => {

    const response = await contactsApi.updateContactFuegoLeadAsync(contactId, flagged);

    if (!response.success) {
      await logError(
        'dialer.contact.updateFuegoLeadError',
        'ContactsVM.updateContact',
        { contactId, error: response.error?.message },
        true,
        true,
      );
      return;
    }

    invalidateContactsCache(latestSelectedContactListIdRef.current ?? undefined);
    await refreshContacts( { force: true });

    await logSuccess(t('dialer.contact.updateFuegoLeadSuccess'), 'ContactsVM.updateContactFuegoLead', { contactId }, true);
  }, [contactsApi, invalidateContactsCache, refreshContacts]);

  const deleteContact = useCallback(async (contactId: string): Promise<void> => {
    const trimmedContactId = contactId.trim();
    if (trimmedContactId === '') {
      return;
    }

    await logInfo('Deleting contact...', 'ContactsVM.deleteContact', { contactId: trimmedContactId }, false);

    const response = await contactsApi.deleteContactAsync(trimmedContactId);
    if (!response.success) {
      await logError(
        response.error?.message ?? t('contacts.toast.error.contactDelete'),
        'ContactsVM.deleteContact',
        { contactId: trimmedContactId, error: response.error?.message },
        true,
        true,
      );
      return;
    }

    invalidateContactsCache(latestSelectedContactListIdRef.current ?? undefined);
    await refreshContacts( { force: true });

    await logSuccess(t('contacts.toast.success.contactDelete'), 'ContactsVM.deleteContact', { contactId: trimmedContactId }, true);
  }, [contactsApi, invalidateContactsCache, refreshContacts]);

  const uploadCsvToContactList = useCallback(async (uploadParams: {
    organizationId: string;
    contactListId: string;
    file: File;
  }): Promise<void> => {
    await logInfo('Uploading CSV...', 'ContactsVM.uploadCsvToContactList', {
      organizationId: uploadParams.organizationId,
      contactListId: uploadParams.contactListId,
      fileName: uploadParams.file?.name,
    }, false);

    const response = await contactsApi.uploadCsvToListAsync(uploadParams);
    if (!response.success) {
      await logError(
        response.error?.message ?? t('contacts.toast.error.csvUpload'),
        'ContactsVM.uploadCsvToContactList',
        { contactListId: uploadParams.contactListId, error: response.error?.message },
        true,
        true,
      );
      return;
    }

    invalidateContactsCache(uploadParams.contactListId);
    await refreshContacts({ force: true });

    await logSuccess(t('contacts.toast.success.csvUpload'), 'ContactsVM.uploadCsvToContactList', { contactListId: uploadParams.contactListId }, true);
  }, [contactsApi, invalidateContactsCache, refreshContacts]);

  const downloadTemplateCsv = useCallback((): void => {
    contactsApi.downloadContactsTemplateCsv();
  }, [contactsApi]);

  const exportContactsToCsv = useCallback((exportParams: { contacts: ContactInfoDto[]; filename?: string }): void => {
    contactsApi.exportContactsOfListToCsv({
      contacts: exportParams.contacts ?? [],
      filename: exportParams.filename,
    });
  }, [contactsApi]);

  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    const isInitialMount = isFirstRenderRef.current;
    if (isInitialMount) {
      isFirstRenderRef.current = false;
    } else {
      setContacts([]);
      setSelectedContactListId(null);
      setContactsLoadingState('IDLE');
      contactsCacheRef.current = {};
    }

    const fetchLists = async (): Promise<void> => {
      await refreshContactLists();
    };


    void fetchLists();

  }, [organizationId, activeTab]);

  useEffect(() => {
    const fetchContacts = async (): Promise<void> => {
      if (selectedContactListId !== null) {
        await refreshContacts( { force: true });
      }
    };

    void fetchContacts();
  }, [selectedContactListId, refreshContacts]);

  return useMemo(() => ({
    organizationId,
    activeTab,
    setActiveTab,
    contactLists,
    contactListsLoadingState,
    totalCsvContactLists,
    selectedContactListId,
    setSelectedContactListId,
    selectedContactList,
    contacts,
    contactsLoadingState,
    totalContacts,
    totalWarnings,
    totalOk,
    refreshContactLists,
    refreshContacts,
    createContactList,
    deleteContactList,
    updateContact,
    updateContactFuegoLead,
    deleteContact,
    uploadCsvToContactList,
    downloadTemplateCsv,
    exportContactsToCsv,
    invalidateContactsCache,
    retrieveCsvLists,
  }), [
    activeTab,
    contactLists,
    contactListsLoadingState,
    totalCsvContactLists,
    selectedContactListId,
    selectedContactList,
    contacts,
    contactsLoadingState,
    totalContacts,
    totalWarnings,
    totalOk,
    refreshContactLists,
    refreshContacts,
    createContactList,
    deleteContactList,
    updateContact,
    updateContactFuegoLead,
    deleteContact,
    uploadCsvToContactList,
    downloadTemplateCsv,
    exportContactsToCsv,
    invalidateContactsCache
  ]);
}
